import moment from "moment";
import { handleActions, Action, ActionMeta } from "redux-actions";
import { IResponse } from "middleware/api";
import { List, Map, OrderedSet } from "immutable";
import { applyTrimOnProduct } from "containers/admin/promotions/utils/helpers";
import {
    ITEM_FAILURE,
    ITEM_INVENTORY,
    ITEM_REQUEST,
    ITEM_SET_CURRENT,
    ITEM_SUCCESS,
    ITEM_VARIENTS,
    ITEM_WAREHOUSE_AVAILABILITY,
    ITEM_WAREHOUSE_CHANGE,
    ITEM_WAREHOUSE_REPLENISHMENT_GROUPS,
    PAGE_LEAVE,
    SCOPE_BRAND_SET,
} from "constants/action-types";

const addARecentlyViewedItem = (list, item) => {
    list = list
        .filter((recentItem) => {
            return recentItem !== item;
        })
        .unshift(item);

    if (list.size > 10) {
        list = list.pop();
    }
    return list;
};

interface IAvailabilityPayload {
    availabilities: string[];
    warehouses: string[];
}

type TReplenishmentGroup = ActionMeta<
    string[],
    {
        itemId: string;
        warehouseId: string;
    }
>;

type TInventory = ActionMeta<
    string[],
    {
        date: moment.Moment;
    }
>;

type TInventoriesByDate = Map<string, List<string>>;

export interface IState {
    availabilities?: List<string>;
    current?: any;
    error?: Error & { code?: number };
    inventoriesByDate: TInventoriesByDate;
    isFetching: boolean;
    recentlyViewed: List<any>;
    replenishmentGroupsByWarehouse: Map<string, OrderedSet<string>>;
    variants?: OrderedSet<string>;
    warehouse?: string;
    warehouses?: OrderedSet<string>;
}
export type IItem = IState;

export const initialState: IState = {
    current: undefined,
    error: undefined,
    inventoriesByDate: Map<string, List<string>>(),
    isFetching: false,
    recentlyViewed: List(),
    replenishmentGroupsByWarehouse: Map<string, OrderedSet<string>>(),
    variants: undefined,
    warehouse: undefined,
    warehouses: undefined,
};

export const itemReducer = handleActions<IState, any>(
    {
        [SCOPE_BRAND_SET]: (): IState => {
            return initialState;
        },
        [ITEM_SET_CURRENT]: (state: IState, { payload }: Action<any>): IState => {
            const { variants } = state;
            return {
                ...state,
                availabilities: undefined,
                current: payload,
                inventoriesByDate: Map<string, List<string>>(),
                recentlyViewed: payload ? addARecentlyViewedItem(state.recentlyViewed, payload) : state.recentlyViewed,
                replenishmentGroupsByWarehouse: state.replenishmentGroupsByWarehouse.clear(),
                variants: variants?.includes(payload?._id) ? variants : undefined,
                warehouse: undefined,
                warehouses: undefined,
            };
        },
        [ITEM_REQUEST]: (state: IState): IState => {
            return {
                ...state,
                error: undefined,
                inventoriesByDate: Map<string, List<string>>(),
                isFetching: true,
                replenishmentGroupsByWarehouse: state.replenishmentGroupsByWarehouse.clear(),
                variants: undefined,
                warehouse: undefined,
                warehouses: undefined,
            };
        },
        [ITEM_SUCCESS]: (state: IState, { data }: IResponse): IState => {
            const current = applyTrimOnProduct(data.entities.items[data.result]);
            return {
                ...state,
                availabilities: undefined,
                current,
                inventoriesByDate: Map<string, List<string>>(),
                isFetching: false,
                recentlyViewed: addARecentlyViewedItem(state.recentlyViewed, current),
                replenishmentGroupsByWarehouse: state.replenishmentGroupsByWarehouse.clear(),
                variants: undefined,
                warehouse: undefined,
                warehouses: undefined,
            };
        },
        [ITEM_FAILURE]: (state: IState, { error }: IResponse): IState => {
            return {
                ...state,
                availabilities: undefined,
                current: undefined,
                error,
                inventoriesByDate: Map<string, List<string>>(),
                isFetching: false,
                replenishmentGroupsByWarehouse: state.replenishmentGroupsByWarehouse.clear(),
                variants: undefined,
                warehouse: undefined,
                warehouses: undefined,
            };
        },
        [ITEM_WAREHOUSE_AVAILABILITY]: (state: IState, { payload }: Action<IAvailabilityPayload>): IState => {
            const { warehouses, availabilities } = payload;
            return {
                ...state,
                availabilities: List(availabilities),
                warehouse: warehouses && warehouses.length ? warehouses[0] : undefined,
                warehouses: OrderedSet(warehouses),
            };
        },
        [ITEM_WAREHOUSE_REPLENISHMENT_GROUPS]: (state: IState, { meta, payload }: TReplenishmentGroup): IState => {
            return {
                ...state,
                replenishmentGroupsByWarehouse: state.replenishmentGroupsByWarehouse.set(
                    meta.warehouseId,
                    OrderedSet(payload)
                ),
            };
        },
        [ITEM_INVENTORY]: (state: IState, { payload, meta }: TInventory): IState => {
            return {
                ...state,
                inventoriesByDate: state.inventoriesByDate.set(meta.date.valueOf().toString(), List(payload)),
            };
        },
        [ITEM_WAREHOUSE_CHANGE]: (state: IState, { payload }: Action<string>): IState => {
            return {
                ...state,
                warehouse: payload,
            };
        },
        [ITEM_VARIENTS]: (state: IState, { payload }: Action<string[]>): IState => {
            return {
                ...state,
                variants: OrderedSet(payload),
            };
        },
        [PAGE_LEAVE]: (): IState => {
            return initialState;
        },
    } as any,
    initialState
);
